import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type WheelEvent } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import type { Replayer, eventWithTime } from 'rrweb';
import 'rrweb/dist/style.css';
import { ApiError, LIVE_KEYS, api, type LiveAction, type LiveControl, type LiveEndReason, type LiveKey, type LiveSession, type LiveStatus } from '../api/client';
import { Button, Chip, Loading } from '../components/ui';
import { articleFor } from '../help';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';

const ENDED: Record<LiveEndReason, (name: string) => string> = {
  declined: (n) => `${n} said not now.`,
  'no-answer': (n) => `${n} didn’t answer. Try again, or reach them another way.`,
  customer: (n) => `${n} stopped sharing.`,
  staff: () => 'You ended live help.',
  'staff-left': () => 'This viewer was closed, so live help ended.',
  lost: (n) => `${n}’s tab closed or lost its connection.`,
  time: () => 'Live help reached its two-hour limit.',
  busy: () => 'Too many live help sessions at once.'
};

interface Status {
  status: LiveStatus;
  control: LiveControl;
  page: string;
  reason: LiveEndReason | null;
}

/** Where on the customer's page a point on screen is, and which element is there. */
interface Hit {
  x: number;
  y: number;
  node: number | null;
  el: Element | null;
}

/**
 * Stitch support's view of a customer's tab: their page, live, with their cursor; point at
 * things, and (once they allow it) click, type and scroll for them. Opens in its own tab.
 */
export function LiveViewer() {
  const { state } = useAuth();
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<LiveSession | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'point' | 'drive'>('point');
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [scale, setScale] = useState(1);
  const [chooser, setChooser] = useState<{ node: number; x: number; y: number; options: { value: string; label: string; selected: boolean }[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const area = useRef<HTMLDivElement>(null);
  const replayer = useRef<Replayer | null>(null);
  const lastMove = useRef(0);
  const typed = useRef('');
  const typeTimer = useRef<number | undefined>(undefined);
  const wheel = useRef({ dx: 0, dy: 0, node: null as number | null, timer: undefined as number | undefined });

  const staff = Boolean(state?.user?.staff);
  const asking = id === 'new';

  // "Join their screen" opens this tab as /live/new?user=…: ask, then carry on at the session's address.
  useEffect(() => {
    if (!staff || !asking) return;
    const user = params.get('user');
    if (!user) return setError('Choose someone to help from Live help.');
    api
      .liveAsk(user)
      .then((r) => navigate(`/live/${r.session.id}`, { replace: true }))
      .catch((err) => setError(err instanceof Error ? err.message : 'That didn’t work.'));
  }, [staff, asking, params, navigate]);

  useEffect(() => {
    if (!staff || asking) return;
    api
      .liveSession(id)
      .then((r) => setSession(r.session))
      .catch((err) => setError(err instanceof ApiError && err.status === 404 ? 'That live help session isn’t here. It may have ended a while ago.' : 'That didn’t load.'));
  }, [staff, asking, id]);

  // The customer's page, live.
  useEffect(() => {
    if (!staff || asking || !session) return;
    let gone = false;
    let rrweb: typeof import('rrweb') | null = null;
    const start = async (events: eventWithTime[]) => {
      rrweb ??= await import('rrweb');
      if (gone || !stage.current) return;
      replayer.current?.destroy();
      stage.current.replaceChildren();
      const r = new rrweb.Replayer([], {
        root: stage.current,
        liveMode: true,
        mouseTail: false,
        showWarning: false,
        triggerFocus: false,
        useVirtualDom: false,
        UNSAFE_replayCanvas: false,
        insertStyleRules: ['html, body { overflow: hidden !important; }']
      });
      r.on('resize', (d) => {
        const { width, height } = d as { width: number; height: number };
        setSize({ w: width, h: height });
      });
      // Everything arrives as it happens, so show it straight away rather than on the customer's clock.
      r.startLive(Number.MAX_SAFE_INTEGER / 2);
      r.disableInteract();
      replayer.current = r;
      for (const e of events) r.addEvent(e);
    };
    const es = new EventSource(`/api/admin/live/sessions/${encodeURIComponent(session.id)}/stream`);
    es.addEventListener('status', (e) => setStatus(JSON.parse((e as MessageEvent<string>).data) as Status));
    es.addEventListener('rr', (e) => {
      const events = JSON.parse((e as MessageEvent<string>).data) as eventWithTime[];
      // A fresh full copy of the page starts a fresh view (and lets the old one go).
      let meta = -1;
      events.forEach((x, i) => {
        if (x.type === 4) meta = i;
      });
      if (meta >= 0) void start(events.slice(meta));
      else for (const x of events) replayer.current?.addEvent(x);
    });
    es.addEventListener('error', () => {
      if (es.readyState === EventSource.CLOSED) setStatus((s) => (s ? { ...s, status: 'ended', reason: s.reason ?? 'lost' } : s));
    });
    return () => {
      gone = true;
      es.close();
      replayer.current?.destroy();
      replayer.current = null;
    };
  }, [staff, asking, session]);

  // Fit their window into ours.
  useEffect(() => {
    const fit = () => {
      const box = area.current?.getBoundingClientRect();
      if (!box || !size) return;
      setScale(Math.min(1, (box.width - 24) / size.w, (box.height - 24) / size.h));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [size]);

  const live = status?.status === 'live';
  const control = status?.control ?? 'none';
  const driving = live && control === 'granted' && mode === 'drive';
  useEffect(() => {
    if (control === 'granted') setMode('drive');
    else setMode('point');
  }, [control]);
  useEffect(() => {
    document.title = `${session ? `Helping ${session.user.name}` : 'Live help'} · Stitch`;
  }, [session]);

  if (!state) return <Loading label="Loading" />;
  if (!staff) return <Navigate to="/" replace />;

  const send = (a: LiveAction) => {
    if (!session || !live) return;
    api.liveAct(session.id, a).catch(() => undefined);
  };

  /** A point on the stage, in the customer's window, with the element of their page under it. */
  const hit = (e: { clientX: number; clientY: number }): Hit | null => {
    const r = replayer.current;
    const box = stage.current?.getBoundingClientRect();
    if (!r || !box) return null;
    const x = (e.clientX - box.left) / scale;
    const y = (e.clientY - box.top) / scale;
    const el = r.iframe.contentDocument?.elementFromPoint(x, y) ?? null;
    const id = el ? r.getMirror().getId(el as unknown as Node) : -1;
    return { x: Math.round(x), y: Math.round(y), node: id > 0 ? id : null, el };
  };

  const onMove = (e: MouseEvent) => {
    const now = Date.now();
    if (now - lastMove.current < 60) return;
    lastMove.current = now;
    const h = hit(e);
    if (h) send({ type: 'pointer', x: h.x, y: h.y });
  };

  const onClick = (e: MouseEvent) => {
    area.current?.focus();
    const h = hit(e);
    if (!h) return;
    if (!driving) return send({ type: 'ping', x: h.x, y: h.y });
    if (!h.node || !h.el) return;
    const select = h.el.closest('select');
    if (select) {
      // A drop-down: pick from its choices here, and it's set on their side.
      const node = replayer.current!.getMirror().getId(select as unknown as Node);
      const options = [...select.querySelectorAll('option')].map((o) => ({ value: o.getAttribute('value') ?? o.textContent ?? '', label: o.textContent ?? '', selected: o.selected }));
      return setChooser({ node, x: e.clientX, y: e.clientY, options });
    }
    send({ type: 'click', node: h.node, x: h.x, y: h.y });
  };

  const flushText = () => {
    typeTimer.current = undefined;
    if (typed.current) send({ type: 'text', text: typed.current });
    typed.current = '';
  };

  const onKey = (e: KeyboardEvent) => {
    if (!driving || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key.length === 1 && e.key !== ' ') {
      e.preventDefault();
      typed.current += e.key;
      if (typeTimer.current === undefined) typeTimer.current = window.setTimeout(flushText, 80);
      return;
    }
    if ((LIVE_KEYS as readonly string[]).includes(e.key)) {
      e.preventDefault();
      flushText();
      send({ type: 'key', key: e.key as LiveKey, ...(e.shiftKey ? { shift: true } : {}) });
    }
  };

  const onWheel = (e: WheelEvent) => {
    if (!driving) return;
    const w = wheel.current;
    w.dx += e.deltaX;
    w.dy += e.deltaY;
    w.node = hit(e)?.node ?? null;
    if (w.timer === undefined)
      w.timer = window.setTimeout(() => {
        send({ type: 'scroll', node: w.node, dx: Math.round(w.dx), dy: Math.round(w.dy) });
        w.dx = 0;
        w.dy = 0;
        w.timer = undefined;
      }, 80);
  };

  async function wantControl(want: boolean) {
    if (!session) return;
    setBusy(true);
    try {
      await api.liveWantControl(session.id, want);
    } finally {
      setBusy(false);
    }
  }

  async function endIt() {
    if (!session) return;
    setBusy(true);
    try {
      await api.liveStop(session.id);
    } finally {
      setBusy(false);
    }
  }

  const name = session?.user.name || session?.user.email || 'them';
  const first = name.split(/\s+/)[0] ?? name;
  const where = status?.page ?? session?.page ?? '';
  const whereTitle = where ? (articleFor(where)?.title ?? where) : '';

  return (
    <div className="lv">
      <header className="lv-top">
        <a href="/admin/live" className="lv-back">
          <Icon name="chevLeft" size={16} /> Live help
        </a>
        <div className="lv-who">
          <strong>{session ? name : asking ? 'Asking to join…' : 'Live help'}</strong>
          {session && (
            <span>
              {[session.company, session.user.email].filter(Boolean).join(' · ')}
              {whereTitle && (
                <>
                  {' · '}On <span title={where}>{whereTitle}</span>
                </>
              )}
            </span>
          )}
        </div>
        <div className="lv-state">
          {status?.status === 'asking' && <Chip tone="warn">Waiting for {first} to say yes</Chip>}
          {live && control === 'none' && <Chip tone="ok">Watching</Chip>}
          {live && control === 'asked' && <Chip tone="warn">Asked {first} for control</Chip>}
          {live && control === 'granted' && <Chip tone="brand">You have control</Chip>}
          {status?.status === 'ended' && <Chip>Ended</Chip>}
        </div>
        <div className="lv-actions">
          {live && control === 'granted' && (
            <div className="segmented lv-mode" role="group" aria-label="Clicks">
              <button type="button" aria-pressed={mode === 'point'} className={mode === 'point' ? 'on' : undefined} onClick={() => setMode('point')}>
                <Icon name="pointer" size={15} /> Point
              </button>
              <button type="button" aria-pressed={mode === 'drive'} className={mode === 'drive' ? 'on' : undefined} onClick={() => setMode('drive')}>
                Click and type
              </button>
            </div>
          )}
          {live && control === 'none' && (
            <Button size="small" variant="secondary" disabled={busy} onClick={() => void wantControl(true)}>
              Ask for control
            </Button>
          )}
          {live && control !== 'none' && (
            <Button size="small" variant="ghost" disabled={busy} onClick={() => void wantControl(false)}>
              {control === 'granted' ? 'Give back control' : 'Stop asking'}
            </Button>
          )}
          {(live || status?.status === 'asking') && (
            <Button size="small" variant="danger" disabled={busy} onClick={() => void endIt()}>
              End
            </Button>
          )}
        </div>
      </header>

      <div className="lv-area" ref={area}>
        {error ? (
          <div className="lv-message">
            <h1>Live help</h1>
            <p>{error}</p>
            <a className="btn secondary" href="/admin/live">
              Back to Live help
            </a>
          </div>
        ) : !session || !status ? (
          <Loading label={asking ? 'Asking' : 'Connecting'} />
        ) : status.status === 'asking' ? (
          <div className="lv-message">
            <span className="lv-pulse" aria-hidden="true" />
            <h1>Waiting for {first} to say yes</h1>
            <p>They see a message asking to let you see their Stitch tab. It goes away by itself after two minutes.</p>
          </div>
        ) : status.status === 'ended' ? (
          <div className="lv-message">
            <h1>Live help has ended</h1>
            <p>{ENDED[status.reason ?? 'lost'](first)} Nothing from the session was kept.</p>
            <a className="btn secondary" href="/admin/live">
              Back to Live help
            </a>
          </div>
        ) : null}
        <div
          className={`lv-screen${live ? '' : ' hidden'}${driving ? ' driving' : ''}`}
          style={size ? { width: size.w * scale, height: size.h * scale } : undefined}
          tabIndex={live ? 0 : -1}
          aria-label={`${name}’s screen`}
          onMouseMove={onMove}
          onMouseLeave={() => send({ type: 'pointer-out' })}
          onClick={onClick}
          onKeyDown={onKey}
          onWheel={onWheel}
          onPaste={(e) => {
            if (!driving) return;
            const text = e.clipboardData.getData('text').slice(0, 500);
            if (text) send({ type: 'text', text });
          }}
        >
          <div className="lv-stage" ref={stage} style={{ transform: `scale(${scale})` }} />
        </div>
        {live && (
          <p className="lv-hint">
            {driving
              ? 'Clicks, typing and scrolling go to their screen. Passwords stay hidden and can’t be typed into.'
              : control === 'granted'
                ? 'Pointing: click to show them where. Choose Click and type to drive.'
                : `Click to show ${first} where to look. Your pointer shows on their screen.`}
          </p>
        )}
        {chooser && (
          <div className="lv-chooser" style={{ left: chooser.x, top: chooser.y }} role="listbox" aria-label="Choose an option">
            {chooser.options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.selected}
                onClick={() => {
                  send({ type: 'choose', node: chooser.node, value: o.value });
                  setChooser(null);
                }}
              >
                {o.label}
              </button>
            ))}
            <button type="button" className="lv-chooser-cancel" onClick={() => setChooser(null)}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
